import { useState, useEffect } from "react";
import { 
  Trophy, Users, Eye, EyeOff, Edit2, Plus, Minus, Save, X, 
  ArrowUp, ArrowDown, Search, Loader2, RefreshCw
} from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function AdminLeaderboardTab({ adminPassword }) {
  const [leaderboard, setLeaderboard] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [editData, setEditData] = useState({});
  const [saving, setSaving] = useState(false);

  const fetchLeaderboard = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/admin/leaderboard`, {
        params: { admin_password: adminPassword }
      });
      setLeaderboard(response.data.leaderboard || []);
    } catch (error) {
      toast.error("Failed to fetch leaderboard");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLeaderboard();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [adminPassword]);

  const handleEdit = (entry) => {
    setEditingId(entry.contributor_id);
    setEditData({
      display_name: entry.display_name || "",
      hidden: entry.hidden || false,
      bonus_points: 0
    });
  };

  const handleSave = async (contributorId) => {
    setSaving(true);
    try {
      await axios.patch(
        `${API}/admin/leaderboard/${contributorId}`,
        editData,
        { params: { admin_password: adminPassword } }
      );
      toast.success("Leaderboard entry updated");
      setEditingId(null);
      fetchLeaderboard();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update");
    } finally {
      setSaving(false);
    }
  };

  const handleToggleVisibility = async (entry) => {
    try {
      await axios.patch(
        `${API}/admin/leaderboard/${entry.contributor_id}`,
        { hidden: !entry.hidden },
        { params: { admin_password: adminPassword } }
      );
      toast.success(entry.hidden ? "Contributor shown on leaderboard" : "Contributor hidden from leaderboard");
      fetchLeaderboard();
    } catch (error) {
      toast.error("Failed to update visibility");
    }
  };

  const filteredLeaderboard = leaderboard.filter(entry => {
    const search = searchTerm.toLowerCase();
    return (
      entry.display_name?.toLowerCase().includes(search) ||
      entry.username?.toLowerCase().includes(search) ||
      entry.name?.toLowerCase().includes(search) ||
      entry.email?.toLowerCase().includes(search)
    );
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-brand" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="font-semibold text-gray-900 flex items-center gap-2">
            <Trophy className="w-5 h-5 text-amber-500" /> Leaderboard Management
          </h3>
          <p className="text-sm text-gray-500 mt-1">
            {leaderboard.length} contributors • {leaderboard.filter(e => !e.hidden).length} visible
          </p>
        </div>
        <button
          onClick={fetchLeaderboard}
          className="flex items-center gap-2 px-3 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
        >
          <RefreshCw className="w-4 h-4" />
          Refresh
        </button>
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
        <input
          type="text"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          placeholder="Search by name, username, or email..."
          className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-brand/20 focus:border-brand"
        />
      </div>

      {/* Leaderboard Table */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-4 py-3 text-left font-medium text-gray-600">Rank</th>
                <th className="px-4 py-3 text-left font-medium text-gray-600">Contributor</th>
                <th className="px-4 py-3 text-left font-medium text-gray-600">Display Name</th>
                <th className="px-4 py-3 text-right font-medium text-gray-600">Monthly Pts</th>
                <th className="px-4 py-3 text-right font-medium text-gray-600">Total Pts</th>
                <th className="px-4 py-3 text-center font-medium text-gray-600">Status</th>
                <th className="px-4 py-3 text-center font-medium text-gray-600">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {filteredLeaderboard.map((entry) => (
                <tr 
                  key={entry.contributor_id} 
                  className={`hover:bg-gray-50 ${entry.hidden ? 'bg-gray-50 opacity-60' : ''}`}
                >
                  <td className="px-4 py-3">
                    <span className={`font-bold ${
                      entry.rank === 1 ? 'text-yellow-600' :
                      entry.rank === 2 ? 'text-gray-500' :
                      entry.rank === 3 ? 'text-amber-600' :
                      'text-gray-400'
                    }`}>
                      #{entry.rank}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full overflow-hidden bg-gray-100 flex items-center justify-center">
                        {entry.avatar ? (
                          <img src={entry.avatar} alt="" className="w-full h-full object-cover" />
                        ) : (
                          <span className="text-sm font-bold text-gray-400">
                            {(entry.username || entry.name)?.[0]?.toUpperCase() || "?"}
                          </span>
                        )}
                      </div>
                      <div>
                        <p className="font-medium text-gray-900">{entry.name || entry.username}</p>
                        <p className="text-xs text-gray-500">{entry.email}</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    {editingId === entry.contributor_id ? (
                      <input
                        type="text"
                        value={editData.display_name}
                        onChange={(e) => setEditData({ ...editData, display_name: e.target.value })}
                        className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                        placeholder="Display name..."
                      />
                    ) : (
                      <span className="text-gray-700">{entry.display_name || "-"}</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-right font-medium text-brand">
                    {entry.monthly_points || 0}
                  </td>
                  <td className="px-4 py-3 text-right">
                    {editingId === entry.contributor_id ? (
                      <div className="flex items-center justify-end gap-1">
                        <span className="font-medium">{entry.total_points}</span>
                        <div className="flex items-center gap-1 ml-2">
                          <button
                            onClick={() => setEditData({ ...editData, bonus_points: editData.bonus_points - 10 })}
                            className="p-1 hover:bg-red-100 rounded text-red-600"
                          >
                            <Minus className="w-3 h-3" />
                          </button>
                          <span className={`text-xs font-medium min-w-[40px] text-center ${
                            editData.bonus_points > 0 ? 'text-green-600' : 
                            editData.bonus_points < 0 ? 'text-red-600' : 'text-gray-400'
                          }`}>
                            {editData.bonus_points > 0 ? '+' : ''}{editData.bonus_points}
                          </span>
                          <button
                            onClick={() => setEditData({ ...editData, bonus_points: editData.bonus_points + 10 })}
                            className="p-1 hover:bg-green-100 rounded text-green-600"
                          >
                            <Plus className="w-3 h-3" />
                          </button>
                        </div>
                      </div>
                    ) : (
                      <span className="font-medium text-gray-900">{entry.total_points || 0}</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-center">
                    {entry.hidden ? (
                      <span className="inline-flex items-center gap-1 px-2 py-1 bg-gray-100 text-gray-600 rounded text-xs">
                        <EyeOff className="w-3 h-3" /> Hidden
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 px-2 py-1 bg-green-100 text-green-700 rounded text-xs">
                        <Eye className="w-3 h-3" /> Visible
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-center gap-1">
                      {editingId === entry.contributor_id ? (
                        <>
                          <button
                            onClick={() => handleSave(entry.contributor_id)}
                            disabled={saving}
                            className="p-1.5 bg-green-100 text-green-700 rounded hover:bg-green-200"
                          >
                            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                          </button>
                          <button
                            onClick={() => setEditingId(null)}
                            className="p-1.5 bg-gray-100 text-gray-600 rounded hover:bg-gray-200"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </>
                      ) : (
                        <>
                          <button
                            onClick={() => handleEdit(entry)}
                            className="p-1.5 text-gray-400 hover:text-brand hover:bg-brand/10 rounded"
                            title="Edit"
                          >
                            <Edit2 className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleToggleVisibility(entry)}
                            className={`p-1.5 rounded ${
                              entry.hidden 
                                ? 'text-gray-400 hover:text-green-600 hover:bg-green-50' 
                                : 'text-gray-400 hover:text-red-600 hover:bg-red-50'
                            }`}
                            title={entry.hidden ? "Show on leaderboard" : "Hide from leaderboard"}
                          >
                            {entry.hidden ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                          </button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {filteredLeaderboard.length === 0 && (
          <div className="text-center py-12">
            <Trophy className="w-12 h-12 mx-auto text-gray-300 mb-3" />
            <p className="text-gray-500">No contributors found</p>
          </div>
        )}
      </div>

      {/* Help Text */}
      <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-sm text-amber-800">
        <strong>Tips:</strong>
        <ul className="list-disc list-inside mt-2 space-y-1">
          <li><strong>Display Name</strong> - Override the name shown on the public leaderboard</li>
          <li><strong>Hidden</strong> - Remove from public leaderboard (e.g., test accounts)</li>
          <li><strong>Bonus Points</strong> - Add or remove points (logged to history)</li>
        </ul>
      </div>
    </div>
  );
}
